import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowLeft, Clock } from "lucide-react";
import { fetchArticle } from "@/lib/blogApi";
import { BlogNav, BlogFooter, MaskedLines, Reveal, ArticleCard, formatDate, BOOKING_URL } from "@/components/blog/shared";
import { openCalendly } from "@/lib/calendly";
import "@/blog.css";

export default function BlogArticle() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    setData(null);
    window.scrollTo({ top: 0 });
    fetchArticle(slug)
      .then(setData)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF7]">
        <BlogNav />
        <div className="container-main pt-44 pb-32" data-testid="article-loading">
          <div className="h-8 w-2/3 bg-[#F0EDE6] rounded animate-pulse mb-6" />
          <div className="h-96 bg-white border border-[#E7E5E4] rounded-2xl animate-pulse" />
        </div>
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="min-h-screen bg-[#FAFAF7]">
        <BlogNav />
        <div className="container-main pt-48 pb-32 text-center" data-testid="article-not-found">
          <p className="font-heading text-6xl font-bold text-[#7C3AED] mb-6">404</p>
          <h1 className="font-heading text-2xl text-[#0A0A0A] mb-6">This article doesn't exist or isn't published.</h1>
          <Link to="/blog" className="text-[#7C3AED] underline underline-offset-4 font-medium" data-testid="back-to-blog-404">
            Back to the journal
          </Link>
        </div>
        <BlogFooter />
      </div>
    );
  }

  const { article, related } = data;
  const canonical = `${window.location.origin}/blog/${article.slug}`;
  const titleWords = article.title.split(" ");
  const mid = Math.ceil(titleWords.length / 2);
  const titleLines = [titleWords.slice(0, mid).join(" "), titleWords.slice(mid).join(" ")].filter(Boolean);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.meta_description || article.excerpt,
    image: article.og_image_url || article.featured_image_url,
    datePublished: article.published_at,
    dateModified: article.updated_at,
    author: { "@type": "Organization", name: article.author_name || "Voveron Team" },
    publisher: { "@type": "Organization", name: "Voveron" },
    mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid="blog-article-page">
      <Helmet>
        <title>{article.seo_title || `${article.title} | Voveron Journal`}</title>
        <meta name="description" content={article.meta_description || article.excerpt} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={article.seo_title || article.title} />
        <meta property="og:description" content={article.meta_description || article.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        {(article.og_image_url || article.featured_image_url) && (
          <meta property="og:image" content={article.og_image_url || article.featured_image_url} />
        )}
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <BlogNav />

      <article className="pt-24 md:pt-32">
        <header className="container-main max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm font-medium text-[#525252] hover:text-[#0A0A0A] transition-colors mb-8 md:mb-10"
              data-testid="back-to-blog"
            >
              <ArrowLeft size={14} /> Back to blog
            </Link>
          </motion.div>
          <div className="flex flex-wrap items-center gap-3 text-[11px] uppercase tracking-widest font-heading font-semibold mb-5 md:mb-6">
            {article.category_name && (
              <Link to={`/blog?category=${article.category_slug}`} className="text-[#7C3AED]" data-testid="article-category">
                {article.category_name}
              </Link>
            )}
            <span className="w-1 h-1 bg-[#E7E5E4] rounded-full" />
            <span className="text-[#737373]" data-testid="article-date">{formatDate(article.published_at)}</span>
            <span className="text-[#737373] flex items-center gap-1.5">
              <Clock size={12} /> {article.reading_time} min read
            </span>
          </div>
          <h1 className="font-heading font-bold text-[#0A0A0A] text-[1.85rem] sm:text-4xl lg:text-[3rem] leading-[1.12] tracking-tight mb-5 md:mb-6" data-testid="article-title">
            <MaskedLines lines={titleLines} delay={0.1} />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="text-[15px] md:text-lg text-[#525252] leading-relaxed mb-5"
            data-testid="article-excerpt"
          >
            {article.excerpt}
          </motion.p>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-sm text-[#737373] mb-10"
          >
            By <span className="text-[#0A0A0A] font-medium">{article.author_name}</span>
          </motion.p>
        </header>

        {article.featured_image_url && (
          <motion.div
            initial={{ clipPath: "inset(0 0 100% 0)" }}
            animate={{ clipPath: "inset(0 0 0% 0)" }}
            transition={{ duration: 1, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="container-main max-w-5xl mx-auto mb-14"
          >
            <img
              src={article.featured_image_url}
              alt={article.featured_image_alt || article.title}
              className="w-full aspect-[21/9] object-cover border border-[#E7E5E4] rounded-2xl"
              data-testid="article-featured-image"
            />
          </motion.div>
        )}

        <div className="container-main max-w-3xl mx-auto">
          <div
            className="article-prose"
            data-testid="article-content"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {article.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-14 pt-8 border-t border-[#E7E5E4]" data-testid="article-tags">
              {article.tags.map((t) => (
                <span key={t} className="text-xs uppercase tracking-widest font-heading font-semibold text-[#525252] bg-white border border-[#E7E5E4] px-3 py-1.5 rounded-full">
                  #{t}
                </span>
              ))}
            </div>
          )}

          <Reveal className="mt-14">
            <div className="border border-[#E7E5E4] bg-white p-8 md:p-10 rounded-2xl shadow-[0_15px_40px_-25px_rgba(10,10,10,0.15)]">
              <h3 className="font-heading text-xl font-bold text-[#0A0A0A] mb-3">Want this applied to your account?</h3>
              <p className="text-sm text-[#525252] leading-relaxed mb-6">
                We run these exact systems for a limited roster of sweepstakes operators. Start with a free acquisition audit.
              </p>
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" onClick={openCalendly}>
                <button className="btn-primary px-6 py-3 text-sm font-semibold rounded-full" data-testid="article-cta-button">
                  Book a Call
                </button>
              </a>
            </div>
          </Reveal>
        </div>

        {related?.length > 0 && (
          <section className="container-main mt-28" data-testid="related-articles">
            <Reveal>
              <div className="chapter-label mb-8">Keep Reading</div>
            </Reveal>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((a, i) => (
                <ArticleCard key={a.id} article={a} index={i} />
              ))}
            </div>
          </section>
        )}
      </article>

      <BlogFooter />
    </div>
  );
}
